import PropTypes from 'prop-types';
import { lazy } from 'react';
import ErrorBoundary from '../../components/shared/ErrorBoundary';
import { componentTypes, manifest } from '../../config/component.config';
import { logger } from '../../helpers/logger';
import { ComponentSpacer } from '../../styles/ComponentSpacer.styles';

const Components = {};

const ComponentModules = import.meta.glob(
  '../../components/global/**/!(*.stories|*.test).{jsx,tsx}',
  {
    eager: false,
  },
);

for (let key in ComponentModules) {
  const fileName = key.slice(key.lastIndexOf('/') + 1, key.lastIndexOf('.'));

  if (fileName) {
    Components[fileName] = lazy(ComponentModules[key]);
  }
}

const ComponentWrapper = ({ component, componentCount, index, withinTab }) => {
  const {
    template: { id },
  } = component;

  // checks component ID is in manifest of all global components in app
  if (!id || !manifest.includes(id)) {
    return null;
  }

  // Determine the correct component to render based on the component's template ID
  const Component = Components[id];

  if (!Component) {
    logger.warn(`No component found for ID`, id);
    return null;
  }

  return (
    <ErrorBoundary componentId={id} componentType={component.componentType}>
      <Component
        {...component.content}
        {...component}
        withinTab={withinTab}
        firstComponent={index === 0}
        lastComponent={index + 1 === componentCount}
      />
      {component.hasPadding && <ComponentSpacer />}
    </ErrorBoundary>
  );
};

ComponentWrapper.propTypes = {
  component: PropTypes.any,
  componentCount: PropTypes.number,
  index: PropTypes.number,
  withinTab: PropTypes.bool,
};

const ComponentSwitch = ({ components, withinTab = false }) => {
  // Assign a componentType to each component
  const componentListWithType =
    components &&
    components.map((component) => ({
      ...component,
      componentType:
        (component &&
          ((component.content && component.content.componentType) ||
            (component.template &&
              component.template.id &&
              componentTypes[component.template.id]))) ||
        'normal',
    }));

  // Assign a style prop to each component based on siblings
  const componentListWithStyles =
    componentListWithType &&
    componentListWithType.map((component, i) => ({
      ...component,
      hasPadding: !!(
        component.componentType === 'block' &&
        componentListWithType[i + 1] &&
        componentListWithType[i + 1].componentType === 'normal'
      ),
    }));

  if (!componentListWithStyles) {
    return null;
  }

  return (
    <>
      {componentListWithStyles?.map((component, index) => (
        <ComponentWrapper
          // Index is a poor choice here but we don't have a better unique identifier for components at the moment. We should add a unique ID to each component in the future.
          key={`${component.template.id}-${index}`}
          component={component}
          componentCount={componentListWithStyles.length}
          index={index}
          withinTab={withinTab}
        />
      ))}
    </>
  );
};

export default ComponentSwitch;

ComponentSwitch.propTypes = {
  components: PropTypes.arrayOf(PropTypes.any),
  withinTab: PropTypes.bool,
};
